<script lang="ts" setup>
import {computed, ref} from 'vue';
import {captureException, captureMessage} from "@sentry/browser";
import IntlTelInput from 'intl-tel-input/vueWithUtils'
import 'intl-tel-input/build/css/intlTelInput.css'
import QuizSecureTag from '@/components/Quiz/QuizSecureTag.vue'
import QuizButton from '@/components/Quiz/QuizButton.vue'
import {type IAagLeadToken, useQuizStore} from '@/stores/quizStore';
import {type IContact} from '@/types/contact';
import {appendData} from '@/services/quizService';
import {titles, countryCodes, countries, defaultCountry} from '@/helpers/constants';
import {gtmLead, mktLeadCreated, mktProspectCreated} from "@/helpers/marketingEvents.ts";
import {type CountryCode, isValidPhoneNumber} from 'libphonenumber-js'

const {
  selectedVehicles,
  contact: storeContact,
  isFetching,
  setContact,
  goToNextStep,
  getStoredData,
  setIsFetching,
  showAlert,
  resetQuiz,
  setAagLeadToken,
  leadSource,
  consentMarketing
} = useQuizStore()

const initialCountry = !storeContact.phone
  ? defaultCountry
  : (Object.keys(countryCodes).find(country => storeContact.phone.startsWith(countryCodes[country as keyof typeof countryCodes])) || defaultCountry)

const contact = ref<IContact>({...storeContact})
const phoneIsValid = ref(false)
const showErrors = ref(false)
const selectedCountry = ref(initialCountry)
const titleIsValid = computed(() => !!contact.value.title)
const firstNameIsValid = computed(() => !!contact.value.first_name)
const lastNameIsValid = computed(() => !!contact.value.last_name)

const titleOptions = computed(() => titles.map(title => ({
  value: title,
  label: title.replace('.', '')
})))

const title = computed(() => {
  return selectedVehicles.length > 1 ? `${selectedVehicles.length} possible <strong>DPF</strong> claims found!` : '1 possible <strong>DPF</strong> claim found!'
})

const isValid = computed(() => titleIsValid.value && firstNameIsValid.value && lastNameIsValid.value && phoneIsValid.value)

function handleChangeNumber(value: string) {
  contact.value.phone = value
  phoneIsValid.value = isValidPhoneNumber(value, selectedCountry.value.toUpperCase() as CountryCode)
}

function handleChangeCountry(country: string) {
  selectedCountry.value = country
}

function handleSubmit() {
  if (!isValid.value) {
    showErrors.value = true
    return
  }

  const payload = getStoredData()
  payload.contact = {
    ...payload.contact,
    ...contact.value
  }
  setIsFetching(true)
  appendData(payload)
    .then(async (response) => {
      const aagLeadToken = response?.aag_lead_token as IAagLeadToken | undefined

      if (response.next_action === "redirect_home") {
        showAlert(response.message)
        await resetQuiz()
        return
      }

      setContact(payload.contact)

      if (aagLeadToken) {
        setAagLeadToken(aagLeadToken)

        aagLeadToken.dpf.forEach((token) => {
          gtmLead(payload.email, payload.contact.phone)
          mktLeadCreated(token, payload.email, payload.contact.phone, payload.contact.first_name,
            payload.contact.last_name, payload.contact.title, leadSource.value, 'DPF', consentMarketing.value)
          mktProspectCreated(token, payload.email, payload.contact.phone, payload.contact.first_name,
            payload.contact.last_name, payload.contact.title, leadSource.value, consentMarketing.value)
        })

        aagLeadToken.pcp.forEach((token) => {
          gtmLead(payload.email, payload.contact.phone)
          mktLeadCreated(token, payload.email, payload.contact.phone, payload.contact.first_name,
            payload.contact.last_name, payload.contact.title, leadSource.value, 'PCP', consentMarketing.value)

          mktProspectCreated(token, payload.email, payload.contact.phone, payload.contact.first_name,
            payload.contact.last_name, payload.contact.title, leadSource.value, consentMarketing.value)
        })
      }

      goToNextStep()
    })
    .catch(async (err) => {
      const response = await err.response?.json()
      if (response?.error) {
        showAlert(response.error)
      } else {
        captureMessage("Error on appendData: ContactForm.vue");
        captureException(err);
      }
    })
    .finally(() => {
      setIsFetching(false)
    })
}
</script>

<template>
  <div v-if="selectedVehicles.length">
    <img src="@/assets/icons/green-check--icon.webp" alt="trophy" class="h-20 mx-auto py-2"/>

    <h1 class="text-2xl font-bold text-(--brand-primary) text-center mb-3" v-html="title"/>

    <p class="text-gray-600 mb-4 font-medium">
      Please enter your details exactly as they appear on your driving licence. This helps us verify
      your DPF claim and continue checking for any other claims that may be linked to your name.
    </p>
  </div>

  <div v-else>
    <h1 class="text-xl font-bold  mb-3">
      <span class="text-(--brand-primary) ">Great Work!</span> Every step moves you closer to
      compensation. Next, confirm your details.
    </h1>

    <p class="text-gray-600 mb-4 font-medium">
      Your details are the key to unlocking claims --- enter them just as they appear on your
      licence.
    </p>
  </div>

  <div class="flex gap-4 mb-4">
    <div class="flex flex-col justify-end">
      <label for="title" class="block text-gray-700 font-medium mb-1">Title*</label>
      <select id="title" v-model="contact.title"
              class="w-full p-2 border border-gray-300 rounded-lg focus:outline-none h-10.5"
              :class="showErrors && !titleIsValid ? 'border-red-500': ''">
        <option value="">Select</option>
        <option v-for="title in titleOptions" :key="title.value" :value="title.value">{{
            title.label
          }}
        </option>
      </select>
    </div>
    <div class="flex flex-col justify-end grow">
      <label for="firstName" class="block text-gray-700 font-medium mb-1">First Name*</label>
      <input type="text" id="firstName" v-model="contact.first_name"
             class="w-full p-2 border border-gray-300 rounded-lg focus:outline-none"
             :class="showErrors && !firstNameIsValid ? 'border-red-500': ''">
    </div>
  </div>

  <div class="grid grid-cols-2 gap-4 mb-4">
    <div class="flex flex-col justify-end">
      <label for="middleName" class="block text-gray-700 font-medium mb-1">Middle Name</label>
      <input type="text" id="middleName" v-model="contact.middle_name"
             class="w-full p-2 border border-gray-300 rounded-lg focus:outline-none"
      >
    </div>
    <div class="flex flex-col justify-end">
      <label for="lastName" class="block text-gray-700 font-medium mb-1">Last Name*</label>
      <input type="text" id="lastName" v-model="contact.last_name"
             class="w-full p-2 border border-gray-300 rounded-lg focus:outline-none"
             :class="showErrors && !lastNameIsValid ? 'border-red-500': ''">
    </div>
  </div>
  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
    <div class="flex flex-col justify-end"
         :class="showErrors && !phoneIsValid ? '*:border-red-500': ''">
      <label for="phone" class="block text-gray-700 font-medium mb-1">Phone*</label>
      <IntlTelInput ref="phoneRef" :options="{
        initialCountry: initialCountry,
        onlyCountries: countries,
        containerClass: 'w-full flex border border-gray-300 rounded-lg px-3'
      }" :inputProps="{
        id: 'phone',
        placeholder: '7400 123456',
        class: 'grow p-2 focus:outline-none',
        nationalMode: false,
      }" :value="contact.phone" @changeNumber="handleChangeNumber"
                    @changeCountry="handleChangeCountry"/>
    </div>
    <div class="flex flex-col justify-end">
      <label for="formerSurname" class="block text-gray-700 font-medium mb-1">Former Surname</label>
      <input type="text" id="formerSurname" v-model="contact.former_surname"
             class="w-full p-2 border border-gray-300 rounded-lg focus:outline-none">
    </div>
  </div>


  <QuizButton label="Continue" :isFetching="isFetching" :disabled="isFetching"
              @click="handleSubmit" id="contact-form-button"/>

  <QuizSecureTag class="mt-6"/>
</template>

<style scoped>
:deep(.iti__selected-country) {
  background-color: var(--color-gray-50);
  border-radius: 8px 0 0 8px;
}
</style>
